/**
 * Profile Page
 *
 * Displays user profile information and allows editing display name
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS } from '../../context/RoleAssumptionContext';
import { updateUserProfile } from '../../services/firestore';
import './Profile.css';

export const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { userProfile, organization, logout, refreshUserProfile } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(userProfile?.display_name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!userProfile) {
    return (
      <div className="profile-page">
        <div className="profile-loading">{t('userProfile.loadingProfile')}</div>
      </div>
    );
  }

  const handleEdit = () => {
    setDisplayName(userProfile.display_name);
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setDisplayName(userProfile.display_name);
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError(t('userProfile.displayNameEmpty'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await updateUserProfile(userProfile.uid, { display_name: displayName.trim() });
      await refreshUserProfile();
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating display name:', err);
      setError(t('userProfile.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return t('userProfile.never');
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1>{t('userProfile.title')}</h1>
        </div>

        <div className="profile-card">
          {/* Avatar Section */}
          <div className="profile-avatar-section">
            {userProfile.photo_url ? (
              <img
                src={userProfile.photo_url}
                alt={userProfile.display_name}
                className="profile-avatar-large"
              />
            ) : (
              <div className="profile-avatar-large profile-avatar-initials">
                {getInitials(userProfile.display_name)}
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="profile-info-section">
            {/* Display Name */}
            <div className="profile-field">
              <label className="profile-label">{t('userProfile.displayName')}</label>
              {isEditing ? (
                <div className="profile-edit-group">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="profile-input"
                    placeholder={t('userProfile.enterYourName')}
                    autoFocus
                  />
                  <div className="profile-edit-actions">
                    <button
                      className="profile-btn profile-btn-secondary"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      {t('userProfile.cancel')}
                    </button>
                    <button
                      className="profile-btn profile-btn-primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? t('userProfile.saving') : t('userProfile.save')}
                    </button>
                  </div>
                  {error && <div className="profile-error">{error}</div>}
                </div>
              ) : (
                <div className="profile-value-group">
                  <span className="profile-value">{userProfile.display_name}</span>
                  <button className="profile-edit-btn" onClick={handleEdit}>
                    {t('userProfile.edit')}
                  </button>
                </div>
              )}
            </div>

            {/* Email */}
            <div className="profile-field">
              <label className="profile-label">{t('userProfile.email')}</label>
              <span className="profile-value">{userProfile.email}</span>
            </div>

            {/* Role */}
            <div className="profile-field">
              <label className="profile-label">{t('userProfile.role')}</label>
              <span className={`profile-role-badge ${userProfile.role || 'pending'}`}>
                {userProfile.role ? t(ROLE_LABELS[userProfile.role]) : t('userProfile.pendingApproval')}
              </span>
            </div>

            {/* Organization */}
            <div className="profile-field">
              <label className="profile-label">{t('userProfile.organization')}</label>
              <span className="profile-value">
                {organization?.name || t('userProfile.noOrganization')}
              </span>
            </div>

            {/* Last Login */}
            <div className="profile-field">
              <label className="profile-label">{t('userProfile.lastLogin')}</label>
              <span className="profile-value profile-value-muted">
                {formatDate(userProfile.last_login)}
              </span>
            </div>

            {/* Auth Provider */}
            <div className="profile-field">
              <label className="profile-label">{t('userProfile.signInMethod')}</label>
              <span className="profile-value">
                {userProfile.auth_provider === 'google' ? (
                  <span className="profile-auth-method">
                    <svg className="google-icon" viewBox="0 0 24 24" width="16" height="16">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t('userProfile.google')}
                  </span>
                ) : (
                  t('userProfile.emailPassword')
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <div className="profile-actions">
          <button className="profile-logout-btn" onClick={handleLogout}>
            {t('userProfile.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
